import React, { useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';

import useResourceVoucher from '../../hooks/useResourceVoucher';
import useResourceVoucherProduct from '../../hooks/useResourceVoucherProduct';
import useBranch from '../../hooks/useBranch';
import useUser from '../../hooks/useUser';
import AuthContext from '../../context/authContext';
import LockIconWrapper from '../components/LockIconWrapper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { isValidEmail } from '../../util/validation';
import { inlinePdfUrl } from '../../util/pdfUrl';

const ISSUE_RIGHT = 'voucher:special:freeGameFlexticket';
const DEFAULT_EXPIRATION = dayjs().add(1, 'year').format('YYYY-MM-DD');
const MIN_REASON_LENGTH = 20;
const MAX_QUANTITY = 5;

// Mirrors the issueReason.code enum on the backend's ResourceVoucher schema.
const REASON_OPTIONS = [
  { code: 'old-system-voucher', label: 'Freispiel aus Altsystem' },
  { code: 'arena-help', label: 'Kunde hat in der Arena ausgeholfen' },
  { code: 'kidscountry-freispiel', label: 'KidsCountry Freispiel' },
];

function branchLabel(branch) {
  return branch?.longName || branch?.locationName || branch?.name || 'Unbekannte Filiale';
}

const FreeGameFlexticketIssue = () => {
  const authCtx = useContext(AuthContext);
  const { issueFreeGameFlextickets, getFreeGameRecipientHistory } = useResourceVoucher();
  const { getAllResourceVoucherProducts } = useResourceVoucherProduct();
  const { getBranches } = useBranch();
  const { getMyBranches } = useUser();

  const recipientNameRef = useRef(null);
  const recipientMailRef = useRef(null);
  const reasonTextRef = useRef(null);
  const quantityRef = useRef(null);
  const expirationDateRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [productId, setProductId] = useState('');
  const [branchId, setBranchId] = useState('');
  // Set when the user is assigned to exactly one branch: the voucher is then tied to
  // that branch automatically and the select is replaced by a read-only note.
  const [fixedBranch, setFixedBranch] = useState(null);
  const [reasonCode, setReasonCode] = useState('old-system-voucher');
  const [sendMail, setSendMail] = useState(true);
  const [recipientHistory, setRecipientHistory] = useState(null);
  // Set when the field is left with an incomplete address, so the hint appears at
  // the field instead of only as a toast on submit.
  const [mailError, setMailError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const hasIssueRight = authCtx.checkRight(ISSUE_RIGHT);

  useEffect(() => {
    if (!hasIssueRight) return;
    const loadProducts = async () => {
      try {
        const loaded = await getAllResourceVoucherProducts();
        setProducts(Array.isArray(loaded) ? loaded : []);
      } catch (error) {
        toast.error('Fehler beim Laden der Flexticket-Produkte');
      }
    };
    loadProducts();
  }, [hasIssueRight]);

  useEffect(() => {
    if (!hasIssueRight) return;
    const loadBranches = async () => {
      try {
        // Exactly one assigned branch means the voucher belongs there — no choice to make.
        const mine = await getMyBranches();
        const assigned = Array.isArray(mine?.branches) ? mine.branches : [];
        if (assigned.length === 1) {
          setFixedBranch(assigned[0]);
          setBranchId(assigned[0]._id);
          return;
        }
        const loaded = await getBranches();
        setBranches(Array.isArray(loaded) ? loaded : []);
      } catch (error) {
        toast.error('Fehler beim Laden der Filialen');
      }
    };
    loadBranches();
  }, [hasIssueRight]);

  if (!hasIssueRight) {
    return <Navigate to='/welcome' replace />;
  }

  const selectedProduct = products.find(product => String(product._id) === String(productId));

  const handleRecipientMailBlur = async () => {
    const mail = String(recipientMailRef.current?.value || '').trim();
    // Only a complete address is looked up: the backend rejects anything else with
    // a 422, and the field shows the hint instead of asking pointlessly.
    setMailError(mail.length > 0 && !isValidEmail(mail));
    if (!isValidEmail(mail)) {
      setRecipientHistory(null);
      return;
    }
    try {
      const history = await getFreeGameRecipientHistory(mail);
      setRecipientHistory(history?.count > 0 ? history : null);
    } catch (error) {
      // A failing lookup must not block the issuance — the warning is advisory.
      setRecipientHistory(null);
    }
  };

  const resetForm = () => {
    setResult(null);
    setRecipientHistory(null);
    setMailError(false);
    setProductId('');
    setReasonCode('old-system-voucher');
    setSendMail(true);
    if (recipientNameRef.current) recipientNameRef.current.value = '';
    if (recipientMailRef.current) recipientMailRef.current.value = '';
    if (reasonTextRef.current) reasonTextRef.current.value = '';
    if (quantityRef.current) quantityRef.current.value = '1';
    if (expirationDateRef.current) expirationDateRef.current.value = DEFAULT_EXPIRATION;
  };

  const handleSubmit = async () => {
    const recipientName = String(recipientNameRef.current?.value || '').trim();
    const recipientMail = String(recipientMailRef.current?.value || '').trim();
    const reasonText = String(reasonTextRef.current?.value || '').trim();
    const expirationDate = String(expirationDateRef.current?.value || '').trim();
    const quantity = Number(quantityRef.current?.value || 1);

    if (!productId) return toast.error('Bitte ein Flexticket-Produkt auswählen');
    if (!branchId) return toast.error('Bitte die Filiale auswählen');
    if (recipientName.length < 2) return toast.error('Bitte den Namen des Kunden angeben');
    if (!isValidEmail(recipientMail)) {
      setMailError(true);
      return toast.error('Bitte eine gültige E-Mail-Adresse angeben');
    }
    if (reasonText.length < MIN_REASON_LENGTH) {
      return toast.error(`Bitte den Grund mit mindestens ${MIN_REASON_LENGTH} Zeichen beschreiben`);
    }
    if (!expirationDate || dayjs(expirationDate).isBefore(dayjs(), 'day')) {
      return toast.error('Bitte ein Gültigkeitsdatum in der Zukunft wählen');
    }
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) {
      return toast.error(`Die Anzahl muss zwischen 1 und ${MAX_QUANTITY} liegen`);
    }

    setIsSubmitting(true);
    try {
      const response = await issueFreeGameFlextickets({
        resourceVoucherProductId: productId,
        branchId,
        recipientName,
        recipientMail,
        expirationDate,
        reasonCode,
        reasonText,
        quantity,
        sendMail,
      });
      setResult(response);
      toast.success(
        quantity > 1
          ? `${quantity} Freispiel-Flextickets ausgestellt`
          : 'Freispiel-Flexticket ausgestellt'
      );
    } catch (error) {
      if (error?.code === 'FREE_GAME_DAILY_LIMIT_REACHED') {
        toast.error('Tageslimit für Freispiel-Flextickets erreicht');
      } else {
        toast.error('Fehler beim Ausstellen des Freispiel-Flextickets');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result) {
    const vouchers = Array.isArray(result.vouchers) ? result.vouchers : [];
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto', ...viewRootTextPrimarySx }}>
        <Typography variant='h5' component='h1' sx={{ mb: 2 }}>
          Freispiel-Flexticket ausgestellt
        </Typography>
        <Card>
          <CardContent>
            <Alert severity='success' sx={{ mb: 2 }}>
              {vouchers.length > 1
                ? `${vouchers.length} Flextickets wurden erstellt.`
                : 'Das Flexticket wurde erstellt.'}
              {result.mailSent ? ' Die PDFs wurden per E-Mail versendet.' : ''}
            </Alert>
            <Alert severity='info' sx={{ mb: 2 }}>
              Die PDF-Links sind aus Sicherheitsgründen nur 5 Minuten gültig. Bitte jetzt öffnen
              und ausdrucken.
            </Alert>
            <Stack spacing={2}>
              {vouchers.map(voucher => (
                <Card key={voucher.voucherId} variant='outlined'>
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ sm: 'center' }}
                      justifyContent='space-between'
                    >
                      <Box>
                        <Typography variant='h6' sx={{ fontFamily: 'monospace' }}>
                          {voucher.voucherCode}
                        </Typography>
                        <Typography variant='body2'>
                          PIN: <strong>{voucher.pin}</strong> · Gültig bis{' '}
                          {dayjs(voucher.expirationDate).format('DD.MM.YYYY')} ·{' '}
                          {voucher.personCount} Personen
                        </Typography>
                      </Box>
                      <Button
                        variant='outlined'
                        startIcon={<OpenInNewIcon />}
                        href={inlinePdfUrl(voucher.preSignedURL)}
                        target='_blank'
                        rel='noreferrer'
                      >
                        PDF öffnen
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            <Button variant='contained' sx={{ mt: 3 }} onClick={resetForm}>
              Weiteres Freispiel ausstellen
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto', ...viewRootTextPrimarySx }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <ConfirmationNumberIcon />
        <Typography variant='h5' component='h1'>
          Freispiel-Flexticket ausstellen
        </Typography>
      </Stack>
      <Typography variant='body2' sx={{ mb: 2 }}>
        Für Kunden, die ein altes Freispiel einlösen möchten. Diese Flextickets sind
        umsatzneutral (0 €), gelten nur in einer Filiale und werden in der Gutscheinstatistik
        gesondert ausgewiesen.
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id='free-game-product-label'>Flexticket-Produkt</InputLabel>
                <Select
                  labelId='free-game-product-label'
                  label='Flexticket-Produkt'
                  value={productId}
                  onChange={event => setProductId(event.target.value)}
                >
                  {products.map(product => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedProduct && (
                <Typography variant='caption' component='p' sx={{ mt: 0.5 }}>
                  {Math.round((selectedProduct.durationMS || 0) / 60000)} Minuten für{' '}
                  {selectedProduct.personCount} Personen · Listenwert{' '}
                  {Number(selectedProduct.basePrice || 0).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  €
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type='date'
                label='Gültig bis'
                inputRef={expirationDateRef}
                defaultValue={DEFAULT_EXPIRATION}
                InputLabelProps={{ shrink: true }}
                helperText='Maximal 3 Jahre in der Zukunft.'
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {fixedBranch ? (
                <TextField
                  fullWidth
                  label='Einlösbar in Filiale'
                  value={branchLabel(fixedBranch)}
                  InputProps={{ readOnly: true }}
                  helperText='Deine zugeordnete Filiale.'
                />
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel id='free-game-branch-label'>Einlösbar in Filiale</InputLabel>
                    <Select
                      labelId='free-game-branch-label'
                      label='Einlösbar in Filiale'
                      value={branchId}
                      onChange={event => setBranchId(event.target.value)}
                    >
                      {branches.map(branch => (
                        <MenuItem key={branch._id} value={branch._id}>
                          {branchLabel(branch)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant='caption' component='p' sx={{ mt: 0.5 }}>
                    Das Flexticket kann ausschließlich in dieser Filiale eingelöst werden.
                  </Typography>
                </>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label='Name des Kunden' inputRef={recipientNameRef} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type='email'
                label='E-Mail des Kunden'
                inputRef={recipientMailRef}
                onBlur={handleRecipientMailBlur}
                error={mailError}
                helperText={
                  mailError
                    ? 'Bitte eine vollständige E-Mail-Adresse eingeben (z.B. kunde@example.com).'
                    : ''
                }
              />
            </Grid>

            {recipientHistory && (
              <Grid item xs={12}>
                <Alert severity='warning' icon={<WarningAmberIcon />}>
                  Dieser Kunde hat bereits {recipientHistory.count}{' '}
                  {recipientHistory.count === 1 ? 'Freispiel-Flexticket' : 'Freispiel-Flextickets'}{' '}
                  erhalten
                  {recipientHistory.lastIssuedAt
                    ? ` (zuletzt am ${dayjs(recipientHistory.lastIssuedAt).format('DD.MM.YYYY')})`
                    : ''}
                  .
                  <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {(recipientHistory.vouchers || []).map(entry => (
                      <Chip
                        key={entry.voucherCode}
                        size='small'
                        label={`${entry.voucherCode} · ${entry.branchName} · ${entry.reasonLabel}`}
                      />
                    ))}
                  </Stack>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id='free-game-reason-label'>Grund</InputLabel>
                <Select
                  labelId='free-game-reason-label'
                  label='Grund'
                  value={reasonCode}
                  onChange={event => setReasonCode(event.target.value)}
                >
                  {REASON_OPTIONS.map(option => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type='number'
                label='Anzahl'
                inputRef={quantityRef}
                defaultValue={1}
                inputProps={{ min: 1, max: MAX_QUANTITY }}
                helperText={`1–${MAX_QUANTITY} Flextickets pro Vorgang.`}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Beschreibung des Grunds'
                inputRef={reasonTextRef}
                helperText={`Pflichtangabe, mindestens ${MIN_REASON_LENGTH} Zeichen. Beschreibe genau, warum der Kunde das Flexticket erhält.`}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendMail}
                    onChange={event => setSendMail(event.target.checked)}
                  />
                }
                label='PDF direkt per E-Mail an den Kunden senden'
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction='row' justifyContent='flex-end'>
                <LockIconWrapper right={ISSUE_RIGHT}>
                  <Button
                    variant='contained'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? <CircularProgress size={16} /> : <ConfirmationNumberIcon />
                    }
                  >
                    Freispiel-Flexticket ausstellen
                  </Button>
                </LockIconWrapper>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FreeGameFlexticketIssue;
